import { MATRIX, PERFIL_LABELS, SIDEBAR_PAGES } from './matrix'
import { APP_NAV_CATALOG, resolveNavItem, type AppNavItem } from './nav'
import { PERFIS, type Perfil, type Permissao } from './types'

export { MATRIX, PERFIL_LABELS, SIDEBAR_PAGES }
export type { Perfil, Permissao, MatrixRow, PerfilMeta } from './types'
export { PERFIS, PERMS } from './types'
export { APP_NAV_CATALOG, REACT_ROUTES, resolveNavItem } from './nav'
export type { AppNavItem } from './nav'

/** @deprecated Use APP_NAV_CATALOG — mantido para imports antigos. */
export const APP_NAV: AppNavItem[] = APP_NAV_CATALOG.filter((i) => i.to).map(resolveNavItem)

export function normalizePerfil(raw: unknown): Perfil {
  const p = String(raw || '').toLowerCase()
  if (p === 'colaborador') return 'colab'
  return (PERFIS as readonly string[]).includes(p) ? (p as Perfil) : 'colab'
}

export function getPermissao(perfil: Perfil | string | null | undefined, page: string): Permissao {
  const p = normalizePerfil(perfil)
  const row = MATRIX[p] || MATRIX.colab
  return row[page] ?? false
}

export function canView(perfil: Perfil | string | null | undefined, page: string) {
  return getPermissao(perfil, page) !== false
}

export function canEdit(perfil: Perfil | string | null | undefined, page: string) {
  return getPermissao(perfil, page) === 'edit'
}

export function isAdmin(perfil: Perfil | string | null | undefined) {
  return normalizePerfil(perfil) === 'admin'
}

export function isRH(perfil: Perfil | string | null | undefined) {
  const p = normalizePerfil(perfil)
  return p === 'rh' || p === 'admin'
}

export function isColab(perfil: Perfil | string | null | undefined) {
  return normalizePerfil(perfil) === 'colab'
}

export function getPerfilMeta(perfil: Perfil | string | null | undefined) {
  return PERFIL_LABELS[normalizePerfil(perfil)]
}

/** Home React conforme perfil (colab → portal; demais → dashboard). */
export function homePath(perfil: Perfil | string | null | undefined) {
  return isColab(perfil) ? '/app/colaborador' : '/app'
}

function itemVisible(perfil: Perfil, item: AppNavItem) {
  if (item.matrixKey === 'dashboard') {
    return (
      canView(perfil, 'dashboard') ||
      canView(perfil, 'command-center') ||
      canView(perfil, 'portal')
    )
  }
  return canView(perfil, item.matrixKey)
}

/**
 * Nav filtrada pela MATRIX.
 * Colab: só portal. Demais: catálogo completo (React + deep-links legado).
 * Deduplica command-center quando Início já cobre /app.
 */
export function navForPerfil(perfil: Perfil | string | null | undefined): AppNavItem[] {
  const p = normalizePerfil(perfil)
  if (p === 'colab') {
    return APP_NAV_CATALOG.filter((item) => item.matrixKey === 'portal').map(resolveNavItem)
  }

  const seen = new Set<string>()
  const out: AppNavItem[] = []

  for (const raw of APP_NAV_CATALOG) {
    const item = resolveNavItem(raw)
    if (!itemVisible(p, item)) continue
    // Evita dois links para /app (Início + Visão executiva)
    if (item.matrixKey === 'command-center' && item.to === '/app') continue
    // Permite o mesmo destino em grupos diferentes (ex.: Cargos no DP e no R&S).
    const key = `${item.group || ''}|${item.label}|${item.to || item.legacyHash || item.matrixKey}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push(item)
  }

  return out
}

/** Colaboradores KPI API: admin/rh/analista/gestor. */
export function canFetchRhKpis(perfil: Perfil | string | null | undefined) {
  const p = normalizePerfil(perfil)
  return p === 'admin' || p === 'rh' || p === 'analista' || p === 'gestor'
}
